/**
 * Promote Policy (Solicitudes tab — TV1-2377 slice 7, on slice 6's handshake)
 *
 * 🚨 ONE COMMAND. The seller's click dispatches `RecordSubGoalPromoted` on the
 * AutomationRequest and NOTHING ELSE. A server-side policy then issues
 * `UnshadowRule` on the Rules aggregate (D2). The UI must never send both: the
 * request-side command is what lets `WithdrawAutomationRequest` tell a live
 * Sub-Goal from an approved one, and a UI that also un-shadowed would race the
 * policy and could un-shadow a rule whose promotion the aggregate refused.
 *
 * 🚨 NO `ruleId`. The command schema accepts one, and we deliberately omit it:
 * the aggregate stamps the id from its OWN derivation (`ruleIdForSubGoal`), so a
 * caller-supplied value could make the recorded rule differ from the one the
 * un-shadow policy actually takes live.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RequestsState } from '../state';
import { RequestsCommand } from '../commands';

export const createPromotePolicy = (
  backend: ClientLibraryType
): Policy<RequestsState, RequestsCommand> => {
  let closed = false;

  return {
    name: 'automation-requests-promote',

    shouldReact: ( prev, next ) =>
      prev.ui.pendingPromotion === null && next.ui.pendingPromotion !== null,

    onStateChange: async ({ next, ctx }) => {
      const pending = next.ui.pendingPromotion;
      if ( !pending ) return;

      const subGoal = next.data.subGoals.find(( sg ) => sg.id === pending.subGoalId );

      try {
        if ( subGoal ) {
          await backend.commands.RecordSubGoalPromoted({
            id: subGoal.requestId,
            body: { subGoalId: subGoal.id }
          } as Parameters<ClientLibraryType['commands']['RecordSubGoalPromoted']>[0]);

          // Read-after-write: the row's status becomes `live`, which is what
          // removes the promote affordance. No optimistic update.
          const subGoals = await backend.queries.AutomationRequests();
          if ( !closed ) {
            ctx.dispatch(
              { type: 'automation/requests/data/setSubGoals', payload: { subGoals: subGoals || []}},
              { source: 'api' }
            );
          }
        }
      } finally {
        if ( !closed ) {
          ctx.dispatch(
            {
              type: 'automation/requests/ui/clearPendingPromotion',
              payload: {} as Record<string, never>
            },
            { source: 'api' }
          );
        }
      }
    },

    cleanup: () => {
      closed = true;
    }
  };
};
